<template>
    <v-container v-if="loadSkeleton">
        <SkeletonLoader ruta="turnos" tipo="table-heading, card, card"></SkeletonLoader>
    </v-container>
    <v-container v-else>
        <!-- Solo se usa en pantallas menores a 960px. De md para arriba el
             detalle se muestra embebido en esta misma vista, sin dialogo. -->
        <DialogoNotificacion ref="refDialogoNotificacion" @irARuta="irARuta" />

        <!-- El contenedor recorta lo que se sale de los costados mientras las
             tarjetas se deslizan. Sin esto aparece scroll horizontal durante la
             animacion, que es justo lo que el proyecto quiere evitar. -->
        <div class="contenedor-transicion">
            <!-- Sin `mode`: los dos conviven durante la animacion y se cruzan.
                 Con out-in quedaba un hueco en blanco en el medio, porque uno
                 terminaba de salir antes de que el otro empezara a entrar. -->
            <transition :name="nombreTransicion">
                <!-- DETALLE embebido: solo en pantallas medianas y mayores. -->
                <v-card v-if="mostrarDetalle" key="detalle" flat>
                    <v-card-title class="text-h5 font-weight-bold">
                        <v-btn icon class="mr-2" @click="volverAlListado">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        Notificaciones
                        <v-spacer></v-spacer>
                    </v-card-title>

                    <v-divider></v-divider>

                    <!-- El componente no trae margen propio: el aire lo pone el contenedor. -->
                    <NotificacionCard :aviso="avisoSeleccionado" class="mt-3" @irARuta="irARuta" />
                </v-card>

                <!-- LISTADO -->
                <v-card v-else key="listado" flat>
                    <v-card-title class="text-h5 font-weight-bold">
                        <v-icon color="primary" class="mr-3" size="34">mdi-bell-outline</v-icon>
                        Notificaciones
                        <v-spacer></v-spacer>
                    </v-card-title>

                    <v-card-text v-if="!hayAvisos" class="py-0">
                        <v-alert type="primary" outlined :value="true" dense class="rounded-0"
                            icon="mdi-check-circle-outline" color="secondary">
                            No hay notificaciones
                        </v-alert>
                    </v-card-text>

                    <v-card-text v-else class="py-0">
                        <v-card v-for="aviso in avisos" :key="aviso.id" flat class="rounded-0  card-aviso"
                            @click="abrirAviso(aviso)">
                            <v-divider></v-divider>
                            <v-row class="d-flex flex-row flex-nowrap align-center ma-0 ">
                                <v-col class="pt-2 pb-0 columna-texto">
                                    <v-card-subtitle class="text-caption font-weight-bold mb-1 px-0 pb-0 pt-1">
                                        {{ formatearFecha(aviso.fecha) }}
                                    </v-card-subtitle>
                                    <v-card-title
                                        class="font-weight-bold text-h6 py-1 px-0 primary--text texto-titulo">
                                        {{ aviso.titulo }}
                                        <!-- <v-chip v-if="!aviso.leida" color="red" text-color="white" x-small
                                            class="ml-2 font-weight-bold">
                                            nuevo
                                        </v-chip> -->
                                    </v-card-title>
                                    <!-- El mensaje se recorta siempre a una linea. El texto
                                         completo se ve al abrir la notificacion. -->
                                    <v-card-text
                                        class="font-weight-regular black--text text-body-2 px-0 py-1 text-truncate">
                                        {{ aviso.mensaje }}
                                    </v-card-text>
                                </v-col>
                                <v-col cols="auto" class="pa-0 pr-1">
                                    <v-icon>mdi-chevron-right</v-icon>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-card-text>
                </v-card>
            </transition>
        </div>
    </v-container>
</template>

<script>
import SkeletonLoader from '@/components/alerts/SkeletonLoader.vue'
import DialogoNotificacion from '@/components/dialogos/DialogoNotificacion.vue'
import NotificacionCard from '@/components/cards/NotificacionCard.vue'
import { mapGetters } from 'vuex'

export default {
    name: 'Notificaciones',

    components: {
        SkeletonLoader,
        DialogoNotificacion,
        NotificacionCard,
    },

    data() {
        return {
            // Solo se usa de md para arriba, donde el detalle reemplaza al
            // listado dentro de esta misma vista.
            avisoSeleccionado: null,
            // La transicion cambia segun hacia donde se navega: entrar a una
            // notificacion trae la tarjeta desde la derecha, volver la saca por
            // la derecha y devuelve el listado desde la izquierda.
            nombreTransicion: 'deslizar-adelante',
        }
    },

    computed: {
        ...mapGetters('avisos', ['avisos']),
        ...mapGetters('auth', ['loadSkeleton']),

        hayAvisos() {
            return this.avisos.length > 0
        },

        // smAndDown es todo lo menor a 960px, el breakpoint md de Vuetify.
        esPantallaChica() {
            return this.$vuetify.breakpoint.smAndDown
        },

        mostrarDetalle() {
            return !this.esPantallaChica && this.avisoSeleccionado !== null
        },
    },

    methods: {
        // La ruta la define el back en cada aviso; aca solo se navega.
        irARuta(aviso) {
            this.$router.push(aviso.ruta).catch(() => { })
        },

        // Solo formato de presentacion: el listado llega tal cual de la API.
        formatearFecha(fecha) {
            if (!fecha) return ''
            const fechaAviso = new Date(fecha)
            if (isNaN(fechaAviso.getTime())) return fecha
            return fechaAviso.toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
        },

        // Menos de 960px abre el dialogo fullscreen. De ahi para arriba el
        // detalle reemplaza al listado en la misma vista.
        abrirAviso(aviso) {
            if (this.esPantallaChica) {
                this.$refs.refDialogoNotificacion.abrirDialogo(aviso)
                return
            }
            this.nombreTransicion = 'deslizar-adelante'
            this.avisoSeleccionado = aviso
        },

        volverAlListado() {
            this.nombreTransicion = 'deslizar-atras'
            this.avisoSeleccionado = null
        },
    },

    watch: {
        // Si achican la ventana con el detalle embebido abierto, esa pantalla ya
        // no contempla ese estado: se vuelve al listado en vez de quedar en un
        // limbo donde no se ve ni el detalle ni la lista.
        esPantallaChica(esChica) {
            if (esChica) {
                this.avisoSeleccionado = null
            }
        },
    },
}
</script>

<style scoped>
/* Sin esto el recorte del mensaje no funciona. Un item flex arranca con
   min-width: auto, o sea que se niega a achicarse mas que su contenido, y
   entonces text-truncate nunca llega a mostrar los puntos suspensivos. */
.columna-texto {
    min-width: 0;
}

/* Los titulos largos cortan en varias lineas; el que se recorta es el mensaje. */
.texto-titulo {
    word-break: break-word;
}

.card-aviso {
    cursor: pointer;
}

/* Recorta el desborde lateral durante el deslizamiento. Sin esto la tarjeta que
   entra o sale ensancha la pagina y aparece scroll horizontal.
   El position: relative es el ancla del absolute que usa la tarjeta que sale. */
.contenedor-transicion {
    overflow-x: hidden;
    position: relative;
}

/* La tarjeta que SALE se saca del flujo normal mientras dura su animacion.
   Es lo que permite que las dos ocupen el mismo lugar y se crucen. Si las dos
   siguieran en el flujo, se apilarian una debajo de la otra y en vez de un
   cruce verias un salto. */
.deslizar-adelante-leave-active,
.deslizar-atras-leave-active {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
}

/* --- Transiciones ---------------------------------------------------------
   Las dos tarjetas se animan a la vez y se cruzan: nunca hay un momento con la
   pantalla vacia.
   ADELANTE (abrir la notificacion): el listado sale hacia la izquierda y el
   detalle entra desde la derecha.
   ATRAS (volver): el detalle sale hacia la derecha y el listado vuelve desde la
   izquierda. El movimiento acompaña a donde va el usuario, que es lo que hace
   que se entienda sin pensarlo. */
.deslizar-adelante-enter-active,
.deslizar-adelante-leave-active,
.deslizar-atras-enter-active,
.deslizar-atras-leave-active {
    transition: transform 0.22s ease, opacity 0.22s ease;
}

.deslizar-adelante-enter {
    transform: translateX(100%);
    opacity: 0;
}

.deslizar-adelante-leave-to {
    transform: translateX(-40%);
    opacity: 0;
}

.deslizar-atras-enter {
    transform: translateX(-40%);
    opacity: 0;
}

.deslizar-atras-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

/* Quien pidio menos movimiento en su sistema operativo no deberia recibir un
   deslizamiento: para esas personas puede causar mareo. Se deja el cambio, pero
   sin desplazamiento. */
@media (prefers-reduced-motion: reduce) {

    .deslizar-adelante-enter-active,
    .deslizar-adelante-leave-active,
    .deslizar-atras-enter-active,
    .deslizar-atras-leave-active {
        transition: opacity 0.15s ease;
    }

    .deslizar-adelante-enter,
    .deslizar-adelante-leave-to,
    .deslizar-atras-enter,
    .deslizar-atras-leave-to {
        transform: none;
    }
}
</style>
